import React, { useMemo, useState } from 'react';
import {
  Download, FileText, Loader2, Lock, MessageSquare, Paperclip, Plus,
  Search, Star, Trash2, Upload, Users,
} from 'lucide-react';
import type { OrbitConversation } from '../../../services/OrbitChatService';
import type { OrbitFile, OrbitFileUploadPolicy } from '../../../hooks/useOrbitFiles';
import type { SaveUserPromptInput, UserPrompt } from '../../../hooks/useUserPrompts';
import type { LeftTab, Prompt } from '../indie-chat.types';
import { HistoryLockIcon, HistorySharedIcon } from '../indie-chat.types';

interface Props {
  activeTab: LeftTab;
  onTabChange: (tab: LeftTab) => void;
  conversations?: OrbitConversation[];
  conversationsLoading?: boolean;
  onSelectConversation?: (conversation: OrbitConversation) => void;
  onConversationVisibilityChange?: (conversation: OrbitConversation, visibility: 'private' | 'shared') => Promise<void> | void;
  onNewConversation?: () => void;
  activeConversationId?: string;
  m1Prompts?: Prompt[];
  clientPrompts?: Prompt[];
  onPromptSelect?: (text: string) => void;
  userPrompts?: UserPrompt[];
  onAddUserPrompt?: (input: string | SaveUserPromptInput) => Promise<void>;
  onDeleteUserPrompt?: (id: number) => Promise<void>;
  files?: OrbitFile[];
  filesLoading?: boolean;
  uploadingFile?: boolean;
  uploadPolicy?: OrbitFileUploadPolicy | null;
  onOpenFilePicker?: () => void;
  onDeleteFile?: (id: number) => Promise<void>;
  onDownloadFile?: (file: OrbitFile) => Promise<void>;
  onIncludeFileInContext?: (file: OrbitFile) => Promise<void>;
  contextFileIds?: number[];
  promptsDisabled?: boolean;
  historyDisabled?: boolean;
}

const TABS: Array<{ id: LeftTab; label: string }> = [
  { id: 'history', label: 'History' },
  { id: 'prompts', label: 'Prompts' },
  { id: 'files', label: 'Files' },
];

const labelForConversation = (conversation: OrbitConversation) => {
  const text = conversation.first_user_message?.replace(/\s+/g, ' ').trim() || 'Untitled conversation';
  return text.length > 54 ? `${text.slice(0, 51)}…` : text;
};

const historyGroup = (conversation: OrbitConversation) => {
  const date = new Date(conversation.last_message_at || conversation.created_at || 0);
  if (!Number.isFinite(date.getTime())) return 'Older';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  if (date >= today) return 'Today';
  if (date >= yesterday) return 'Yesterday';
  if (date >= lastWeek) return 'Last week';
  return 'Older';
};

const V3ChatLeftSidebar: React.FC<Props> = ({
  activeTab, onTabChange, conversations = [], conversationsLoading, onSelectConversation,
  onConversationVisibilityChange, onNewConversation, activeConversationId, m1Prompts = [], clientPrompts = [],
  onPromptSelect, userPrompts = [], onAddUserPrompt, onDeleteUserPrompt, files = [],
  filesLoading, uploadingFile, uploadPolicy, onOpenFilePicker, onDeleteFile,
  onDownloadFile, onIncludeFileInContext, contextFileIds = [], promptsDisabled = false, historyDisabled = false,
}) => {
  const [search, setSearch] = useState('');
  const [addingPrompt, setAddingPrompt] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [privacyMenuConversationId, setPrivacyMenuConversationId] = useState<string | null>(null);
  const [updatingVisibilityConversationId, setUpdatingVisibilityConversationId] = useState<string | null>(null);
  const includedFileIds = useMemo(() => new Set(contextFileIds), [contextFileIds]);
  const filteredConversations = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? conversations.filter((conversation) => labelForConversation(conversation).toLowerCase().includes(needle)) : conversations;
  }, [conversations, search]);

  const addPrompt = async () => {
    if (!draftPrompt.trim() || savingPrompt || !onAddUserPrompt) return;
    setSavingPrompt(true);
    try {
      await onAddUserPrompt(draftPrompt.trim());
      setDraftPrompt('');
      setAddingPrompt(false);
    } finally {
      setSavingPrompt(false);
    }
  };

  const updateVisibility = async (conversation: OrbitConversation, visibility: 'private' | 'shared') => {
    if (!onConversationVisibilityChange || updatingVisibilityConversationId) return;
    setUpdatingVisibilityConversationId(conversation.conversation_id);
    try {
      await onConversationVisibilityChange(conversation, visibility);
      setPrivacyMenuConversationId(null);
    } finally {
      setUpdatingVisibilityConversationId(null);
    }
  };

  const promptRow = (prompt: Prompt | UserPrompt, personal = false) => (
    <div key={prompt.id} className="group flex items-center gap-2 px-3.5 py-1.5 hover:bg-[#F9FAFB]">
      <button type="button" disabled={promptsDisabled} onClick={() => onPromptSelect?.('prompt_text' in prompt ? (prompt.prompt_text || prompt.label) : prompt.label)} className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded" style={'iconBg' in prompt ? { background: prompt.iconBg } : { background: '#E0E7FF' }}>
          {'icon' in prompt ? prompt.icon : <Star className="h-2.5 w-2.5 text-[#534AB7]" />}
        </span>
        <span className="truncate text-[11px] text-[#344054]">{prompt.label}</span>
        {personal && <span className="rounded bg-[#E0E7FF] px-1 py-px text-[9px] font-semibold text-[#534AB7]">Mine</span>}
      </button>
      {personal && <button type="button" onClick={() => void onDeleteUserPrompt?.(prompt.id)} aria-label={`Delete ${prompt.label}`} className="hidden rounded p-0.5 text-[#D92D20] hover:bg-[#FEF3F2] group-hover:block"><Trash2 className="h-3 w-3" /></button>}
    </div>
  );

  return (
    <aside className="indie-chat flex h-full w-[240px] shrink-0 flex-col overflow-hidden border-r border-[#EAECF0] bg-white" aria-label="Chat history and resources">
      <div className="flex items-center gap-2 p-3.5">
        <label className="flex h-[34px] flex-1 items-center gap-2 rounded-lg border border-[#D0D5DD] bg-white px-2.5 shadow-2xs">
          <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            type="search"
            placeholder="Search"
            aria-label="Search conversations"
            className="w-full min-w-0 bg-transparent text-[13px] text-[#344054] placeholder:text-[#98A2B3] outline-none"
          />
        </label>
        <button
          type="button"
          onClick={onNewConversation}
          className="flex h-[34px] shrink-0 items-center gap-1 rounded-lg bg-[#6366F1] px-3 text-xs font-medium text-white shadow-2xs transition-colors hover:bg-[#4F46E5]"
        >
          <Plus className="h-3.5 w-3.5" />
          New
        </button>
      </div>
      <div className="flex border-b border-[#EAECF0] px-4 gap-4">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange(tab.id)}
            className={`border-b-2 py-2.5 text-[12px] transition-colors ${activeTab === tab.id
              ? 'border-[#101828] font-semibold text-[#101828]'
              : 'border-transparent font-medium text-[#98A2B3] hover:text-[#667085]'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'history' && (conversationsLoading && !conversations.length ? (
          <div className="flex items-center justify-center gap-2 py-8 text-[11px] text-[#98A2B3]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading…
          </div>
        ) : !filteredConversations.length ? (
          <div className="px-5 py-8 text-center text-[11px] text-[#98A2B3]">
            <MessageSquare className="mx-auto mb-2 h-5 w-5 text-[#D0D5DD]" />
            No conversations found
          </div>
        ) : ['Today', 'Yesterday', 'Last week', 'Older'].map((group) => {
          const items = filteredConversations.filter((conversation) => historyGroup(conversation) === group);
          return !items.length ? null : (
            <div key={group}>
              <p className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.05em] text-[#98A2B3]">{group}</p>
              {items.map((conversation) => {
                const isShared = conversation.conversation_visibility === 'shared';
                const isMenuOpen = privacyMenuConversationId === conversation.conversation_id;
                const isUpdating = updatingVisibilityConversationId === conversation.conversation_id;
                return (
                  <div
                    key={conversation.conversation_id}
                    className={`relative flex items-center justify-between gap-2 px-4 py-2 hover:bg-[#F9FAFB] transition-colors ${conversation.conversation_id === activeConversationId ? 'bg-[#F9FAFB]' : ''
                      }`}
                  >
                    <button
                      type="button"
                      disabled={historyDisabled}
                      onClick={() => onSelectConversation?.(conversation)}
                      className="min-w-0 flex-1 text-left disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <span className="block truncate font-geist text-[14px] font-medium leading-[20px] text-[#344054]">
                        {labelForConversation(conversation)}
                      </span>
                      <span className="block truncate font-geist text-[12px] font-normal leading-[18px] text-[#667085] mt-0.5">
                        {isShared ? 'Shared with company' : 'Private conversation'}
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={isShared ? 'Shared conversation privacy menu' : 'Private conversation privacy menu'}
                      aria-haspopup="menu"
                      aria-expanded={isMenuOpen}
                      disabled={isUpdating}
                      onClick={() => setPrivacyMenuConversationId(isMenuOpen ? null : conversation.conversation_id)}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-50 ${isShared ? 'bg-[#F0F9FF] text-[#0086C9] hover:bg-[#E0F2FE]' : 'bg-[#F2F4F7] text-[#344054] hover:bg-[#EAECF0]'
                        }`}
                    >
                      {isShared ? <HistorySharedIcon /> : <HistoryLockIcon />}
                    </button>
                    {isMenuOpen && (
                      <div role="menu" className="absolute right-3 top-8 z-50 w-48 rounded-lg border border-[#EAECF0] bg-white p-1 shadow-lg">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void updateVisibility(conversation, 'private')}
                          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[11px] hover:bg-[#F9FAFB] ${!isShared ? 'bg-[#F9FAFB] text-[#101828]' : 'text-[#667085]'
                            }`}
                        >
                          <Lock className="h-3 w-3 text-[#344054]" />
                          <span>
                            <strong className="block font-medium text-[#344054]">Private</strong>
                            <span className="text-[10px] text-[#667085]">Only you can access this chat</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => void updateVisibility(conversation, 'shared')}
                          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[11px] hover:bg-[#F9FAFB] ${isShared ? 'bg-[#F9FAFB] text-[#101828]' : 'text-[#667085]'
                            }`}
                        >
                          <Users className="h-3 w-3 text-[#0086C9]" />
                          <span>
                            <strong className="block font-medium text-[#344054]">Shared</strong>
                            <span className="text-[10px] text-[#667085]">Shared with company</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }))}
        {activeTab === 'prompts' && <>{!promptsDisabled && <><p className="px-3.5 pb-1 pt-2.5 text-[9px] font-semibold uppercase tracking-[.98px] text-[#98A2B3]">M1 Prompts</p>{m1Prompts.map((prompt) => promptRow(prompt))}{clientPrompts.length > 0 && <><p className="px-3.5 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-[.98px] text-[#98A2B3]">Client Prompts</p>{clientPrompts.map((prompt) => promptRow(prompt))}</>}</>}<div className="flex items-center justify-between px-3.5 pb-1 pt-3"><p className="text-[9px] font-semibold uppercase tracking-[.98px] text-[#98A2B3]">My Prompts</p><button type="button" onClick={() => setAddingPrompt(true)} className="text-[10px] font-semibold text-[#534AB7]">+ Add</button></div>{addingPrompt && <div className="flex gap-1 px-3.5 py-1"><input autoFocus value={draftPrompt} onChange={(event) => setDraftPrompt(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void addPrompt(); if (event.key === 'Escape') setAddingPrompt(false); }} className="min-w-0 flex-1 rounded border border-[#D0D5DD] px-2 py-1 text-[11px]" placeholder="Type a prompt…" /><button type="button" disabled={savingPrompt} onClick={() => void addPrompt()} className="rounded bg-[#6366F1] px-2 text-xs text-white">Save</button></div>}{promptsDisabled && <p className="px-3.5 py-1 text-[10px] text-[#98A2B3]">Prompt shortcuts are unavailable while the Ask Indie maintenance banner is active.</p>}{userPrompts.map((prompt) => promptRow(prompt, true))}</>}
        {activeTab === 'history' && historyDisabled && conversations.length > 0 && <p className="px-3.5 py-2 text-[10px] text-[#98A2B3]">Conversation history cannot be opened while the Ask Indie maintenance banner is active.</p>}
        {activeTab === 'files' && <div className="p-3.5"><button type="button" disabled={uploadingFile || uploadPolicy?.files_remaining_this_month === 0} onClick={onOpenFilePicker} className="flex w-full items-center justify-center gap-1.5 rounded-md border border-[#C3BFFA] bg-[#EEEDFE] py-2 text-[11px] font-medium text-[#534AB7] disabled:opacity-50"><Upload className="h-3.5 w-3.5" />{uploadingFile ? 'Uploading…' : 'Upload file'}</button>{filesLoading ? <Loader2 className="mx-auto my-6 h-4 w-4 animate-spin text-[#98A2B3]" /> : files.map((file) => { const included = includedFileIds.has(file.id); return <div key={file.id} className="mt-2 flex items-center gap-2"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[#EAECF0] bg-[#F9FAFB]"><FileText className="h-3 w-3 text-[#667085]" /></span><span className="min-w-0 flex-1"><span className="block truncate text-[11px] text-[#101828]">{file.original_filename}</span><span className="block truncate text-[10px] text-[#98A2B3]">{file.file_size_label}</span></span><button type="button" disabled={included || file.context_status !== 'available'} onClick={() => void onIncludeFileInContext?.(file)} title="Add to chat context" className="p-1 text-[#667085] disabled:opacity-40"><Paperclip className="h-3 w-3" /></button><button type="button" onClick={() => void onDownloadFile?.(file)} title="Download" className="p-1 text-[#667085]"><Download className="h-3 w-3" /></button><button type="button" onClick={() => void onDeleteFile?.(file.id)} title="Delete" className="p-1 text-[#D92D20]"><Trash2 className="h-3 w-3" /></button></div>; })}</div>}
      </div>
    </aside>
  );
};

export default V3ChatLeftSidebar;

